<template>
  <v-dialog
    v-model="dialogOpen"
    width="520"
    content-class="ex-propose-changes-active-ml-dialog"
    @click:outside="onClose"
  >
    <v-card
      class="pa-6 ex-propose-changes-active-ml background-pattern-icon"
      data-testid="ex-propose-changes-to-active-microlearning-dialog"
    >
      <div class="d-flex align-start justify-end propose-changes-header">
        <span class="propose-changes-close align-self-start" @click="onClose">
          <v-btn icon color="grey" aria-label="Close">
            <Icon class="cursor-pointer" name="popup_close" />
          </v-btn>
        </span>
      </div>

      <h3 class="propose-changes-title">Propose Changes</h3>
      <p class="propose-changes-description">
        You will be able to publish your changes to replace the current active
        {{ activeContentLabel }} content.
      </p>

      <div class="access-list">
        <div
          v-for="opt in options"
          :key="opt.value"
          class="access-item d-flex align-start"
          :class="{ 'disabled-item': opt.disabled, 'cursor-pointer': !opt.disabled }"
          role="radio"
          :aria-checked="selectedOption === opt.value"
          :aria-disabled="opt.disabled"
          :tabindex="opt.disabled ? -1 : 0"
          @click="selectOption(opt)"
          @keydown.enter.prevent="selectOption(opt)"
          @keydown.space.prevent="selectOption(opt)"
        >
          <div class="propose-changes-option__radio">
            <v-icon v-if="selectedOption === opt.value" small> $exRadioMiniChecked </v-icon>
            <v-icon v-else small>$exRadioMiniNormal</v-icon>
          </div>
          <span class="access-name">{{ opt.label }}</span>
        </div>
      </div>

      <div class="propose-changes-footer d-flex">
        <div class="propose-changes-footer__btn">
          <StandardButton
            type="magenta"
            variant="outline"
            size="full-width"
            label="Cancel"
            @click="onClose"
          />
        </div>
        <div class="propose-changes-footer__btn">
          <StandardButton
            type="magenta"
            variant="primary"
            size="full-width"
            label="Start"
            @click="onStart"
          />
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

const PROPOSAL_OPTIONS = {
  COPY_ACTIVE: "copy_active",
  NEW_VIDEO: "new_video",
  NEW_ARTICLE: "new_article",
  FROM_SCRATCH: "from_scratch",
};

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  activeContentLabel: {
    type: String,
    default: "Microlearn-Test-Inspect",
  },
  hasActiveMicrolearning: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["input", "start"]);

function getDefaultOption() {
  return props.hasActiveMicrolearning
    ? PROPOSAL_OPTIONS.COPY_ACTIVE
    : PROPOSAL_OPTIONS.NEW_VIDEO;
}

const selectedOption = ref(getDefaultOption());

const dialogOpen = computed({
  get: () => props.value,
  set: (v) => emit("input", v),
});

const options = computed(() => [
  {
    value: PROPOSAL_OPTIONS.COPY_ACTIVE,
    label: `Copy the active ${props.activeContentLabel} content`,
    disabled: !props.hasActiveMicrolearning,
  },
  {
    value: PROPOSAL_OPTIONS.NEW_VIDEO,
    label: "Start from a new Video",
  },
  {
    value: PROPOSAL_OPTIONS.NEW_ARTICLE,
    label: "Start from a new Article",
  },
  {
    value: PROPOSAL_OPTIONS.FROM_SCRATCH,
    label: "Start from scratch",
  },
]);

watch(
  () => props.value,
  (open) => {
    if (open) {
      selectedOption.value = getDefaultOption();
    }
  }
);

function selectOption(opt) {
  if (opt.disabled) return;
  selectedOption.value = opt.value;
}

function onClose() {
  emit("input", false);
}

function onStart() {
  emit("start", { option: selectedOption.value });
  emit("input", false);
}
</script>

<style scoped>
::v-deep(.ex-propose-changes-active-ml-dialog) {
  border-radius: 12px !important;
}

.ex-propose-changes-active-ml {
  border-radius: 12px !important;
  overflow: hidden;
}

.background-pattern-icon {
  background-image: url("@components/assets/backgrounds/background_popup_icon.svg");
  background-size: 216px 216px;
  background-position: 0 0;
}

.propose-changes-header {
  margin: -8px -8px 0 0;
}

.propose-changes-close {
  line-height: 0;
  cursor: pointer;
}

.propose-changes-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
  color: #101828;
  margin: 0 0 8px;
}

.propose-changes-description {
  margin: 0 0 20px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #475467;
}

.propose-changes-option__radio {
  flex-shrink: 0;
  margin-right: 14px;
  padding-top: 1px;
  line-height: 0;
}

.cursor-pointer {
  cursor: pointer;
}

.disabled-item {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.ex-propose-changes-active-ml .access-list .access-item {
  border: none;
  border-radius: 0;
  box-shadow: none;
  padding: 0;
  margin: 0;
}

.ex-propose-changes-active-ml .access-list .access-item:not(:last-child) {
  margin-bottom: 12px;
}

.ex-propose-changes-active-ml .access-item .access-name {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.propose-changes-footer {
  margin-top: 32px;
  gap: 12px;
}

.propose-changes-footer__btn {
  flex: 1;
  min-width: 0;
}
</style>
